'use client';

import React, { useState, useEffect } from 'react';

import { useRouter } from 'next/navigation';
import { Video, formatViews } from '@/data/videos';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface VideoCardProps {
  video: Video;
  onClick?: (video: Video) => void;
  size?: 'normal' | 'featured';
  isTrending?: boolean;
}

export default function VideoCard({ video, onClick, size = 'normal', isTrending = false }: VideoCardProps) {
  const router = useRouter();
  const [imgError, setImgError] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [liveViews, setLiveViews] = useState<number>(video.views);
  const [viewsUpdated, setViewsUpdated] = useState(false);

  const { user } = useAuth();
  const supabase = createClient();

  const fallbackThumbnail = 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';

  // Treat data: URLs (broken/truncated base64 from failed uploads) as errors
  const thumbnailSrc = imgError || !video.thumbnail || video.thumbnail.startsWith('data:')
    ? fallbackThumbnail
    : video.thumbnail;

  const videoUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/video/${video.id}`
    : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://viddstar.com'}/video/${video.id}`;

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const fetchLikes = async () => {
      const { count } = await supabase
        .from('video_likes')
        .select('*', { count: 'exact', head: true })
        .eq('video_id', video.id);
      setLikeCount((video.likesCount ?? 0) + (count ?? 0));

      if (user) {
        const { data } = await supabase
          .from('video_likes')
          .select('id')
          .eq('video_id', video.id)
          .eq('user_id', user.id)
          .maybeSingle();
        setLiked(!!data);
      }
    };

    fetchLikes();

    // Real-time subscription for like count updates
    channel = supabase
      .channel(`video_likes_${video.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'video_likes', filter: `video_id=eq.${video.id}` },
        () => { fetchLikes(); }
      )
      .subscribe();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [video.id, user]);

  // Live view count subscription
  useEffect(() => {
    setLiveViews(video.views);

    const viewsChannel = supabase
      .channel(`video_views_${video.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'videos', filter: `id=eq.${video.id}` },
        (payload: any) => {
          const newViews = payload.new?.views;
          if (typeof newViews === 'number' && newViews !== liveViews) {
            setLiveViews(newViews);
            setViewsUpdated(true);
            setTimeout(() => setViewsUpdated(false), 1500);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(viewsChannel);
    };
  }, [video.id, video.views]);

  const handleCardClick = () => {
    if (onClick) {
      onClick(video);
    } else {
      router.push(`/video/${video.id}`);
    }
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || likeLoading) return;
    setLikeLoading(true);
    try {
      if (liked) {
        await supabase
          .from('video_likes')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id);
        setLiked(false);
        setLikeCount((prev) => Math.max(0, prev - 1));
      } else {
        await supabase
          .from('video_likes')
          .insert({ video_id: video.id, user_id: user.id });
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } finally {
      setLikeLoading(false);
    }
  };

  const handleShare = (e: React.MouseEvent, platform: string) => {
    e.stopPropagation();
    const encodedUrl = encodeURIComponent(videoUrl);
    const encodedTitle = encodeURIComponent(video.title);

    const urls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(videoUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
      return;
    }

    window.open(urls[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  return (
    <div
      className="video-card group cursor-pointer"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
      aria-label={`Watch: ${video.title}`}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden bg-surface-tertiary" style={{ aspectRatio: '16/9' }}>
        <img
          src={thumbnailSrc}
          alt={video.thumbnailAlt}
          className="video-thumb w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
          onError={() => setImgError(true)}
          loading="lazy"
        />
        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
        {/* Category badge */}
        <div className="absolute top-2 left-2">
          <span className="bg-white bg-opacity-80 text-text-secondary text-xs px-2 py-0.5 uppercase tracking-wide">
            {video.category}
          </span>
        </div>
        {/* Trending badge */}
        {isTrending && (
          <div className="absolute top-2 right-2">
            <span className="flex items-center gap-1 bg-brand-red text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow-md">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2C9.5 6 8 8.5 8 11a4 4 0 008 0c0-2.5-1.5-5-4-9zm0 13a2 2 0 01-2-2c0-1.2.7-2.5 2-4.5 1.3 2 2 3.3 2 4.5a2 2 0 01-2 2z"/>
              </svg>
              Trending
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-0 pt-2">
        <h2 className="text-text-primary text-sm sm:text-base leading-snug mb-1.5 line-clamp-2">
          <span className="text-text-primary">{video.title}</span>
        </h2>
        <div className="flex items-center gap-2 sm:gap-3 text-text-secondary text-xs sm:text-sm">
          <span
            className={`flex items-center gap-1 transition-colors duration-300 ${viewsUpdated ? 'text-brand-red font-semibold' : ''}`}
            title="Views"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {formatViews(liveViews)}
          </span>
          {/* Like button */}
          <button
            onClick={handleLike}
            disabled={!user || likeLoading}
            className={`flex items-center gap-1 transition-colors tap-compact ${
              liked
                ? 'text-brand-red'
                : user
                ? 'hover:text-brand-red' : 'opacity-60 cursor-default'
            }`}
            aria-label={liked ? 'Unlike video' : 'Like video'}
            title={!user ? 'Sign in to like' : liked ? 'Unlike' : 'Like'}
          >
            <svg
              className="w-3.5 h-3.5"
              fill={liked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
            </svg>
            <span>{formatViews(likeCount)}</span>
          </button>
        </div>

        {/* Tags + Share row */}
        <div className="flex items-center justify-between mt-2 gap-2 tap-compact" onClick={(e) => e.stopPropagation()}>
          {/* Tag */}
          <div className="flex-1 min-w-0">
            {video.tag && (
              <span className="tag-label text-xs">{video.tag}</span>
            )}
          </div>

          {/* Share buttons — larger hit areas on touch */}
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            {/* Facebook */}
            <button
              onClick={(e) => handleShare(e, 'facebook')}
              className="w-8 h-8 flex items-center justify-center rounded text-text-secondary hover:text-[#1877F2] transition-colors"
              title="Share on Facebook"
              aria-label="Share on Facebook"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
            {/* Twitter/X */}
            <button
              onClick={(e) => handleShare(e, 'twitter')}
              className="w-8 h-8 flex items-center justify-center rounded text-text-secondary hover:text-[#1DA1F2] transition-colors"
              title="Share on X (Twitter)"
              aria-label="Share on X"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </button>
            {/* WhatsApp */}
            <button
              onClick={(e) => handleShare(e, 'whatsapp')}
              className="w-8 h-8 flex items-center justify-center rounded text-text-secondary hover:text-[#25D366] transition-colors"
              title="Share on WhatsApp"
              aria-label="Share on WhatsApp"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
            {/* Copy link */}
            <button
              onClick={(e) => handleShare(e, 'copy')}
              className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${copied ? 'text-green-500' : 'text-text-secondary hover:text-text-primary'}`}
              title={copied ? 'Copied!' : 'Copy link'}
              aria-label="Copy link"
            >
              {copied ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
